/**
 * Fondo decorativo compartido por la portada, las pantallas de acceso y la app.
 *
 * Todo es CSS y SVG en línea: la CSP solo admite imágenes del propio origen
 * (`img-src 'self' data: blob:`), así que una ilustración externa se quedaría
 * en blanco sin avisar.
 *
 * Se pinta con `-z-10`, de modo que el contenedor que lo use solo necesita ser
 * `relative isolate` para que el contenido quede por encima.
 */

import clsx from 'clsx';
import { useId } from 'react';

export type BackgroundVariant = 'hero' | 'app';

export interface BackgroundProps {
  /**
   * `hero` para la portada, con las manchas de color bien visibles.
   * `app` para pantallas de trabajo (acceso, panel, administración): el mismo
   * fondo, pero atenuado, para que las tablas y los formularios manden.
   */
  variant?: BackgroundVariant;
}

export function Background({ variant = 'hero' }: BackgroundProps) {
  const patternId = useId();
  const isHero = variant === 'hero';

  return (
    <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden" aria-hidden>
      <div className="absolute inset-0 bg-gradient-to-b from-sky-50 via-white to-emerald-50/60" />

      <div
        className={clsx(
          'absolute -left-24 -top-24 h-72 w-72 rounded-full blur-3xl',
          isHero ? 'bg-brand-blue/20' : 'bg-brand-blue/10',
        )}
      />
      <div
        className={clsx(
          'absolute -right-20 top-24 h-80 w-80 rounded-full blur-3xl',
          isHero ? 'bg-brand-green/20' : 'bg-brand-green/10',
        )}
      />
      {/* La tercera mancha solo en la portada: en una tabla larga distrae. */}
      {isHero && (
        <div className="absolute left-1/3 top-[28rem] h-64 w-64 rounded-full bg-brand-yellow/20 blur-3xl" />
      )}

      {/* Trama diagonal muy tenue, para que el blanco no quede plano. */}
      <svg className={clsx('absolute inset-0 h-full w-full', isHero ? 'opacity-35' : 'opacity-20')}>
        <defs>
          <pattern id={patternId} width="28" height="28" patternUnits="userSpaceOnUse">
            <path d="M0 28 L28 0" stroke="#94a3b8" strokeWidth="0.5" opacity="0.25" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill={`url(#${patternId})`} />
      </svg>
    </div>
  );
}
